import Label from "../../../../../ui/Label/Label";
import ConfirmationModal from "../../../../../ui/Modal/ConfirmationModal";
import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../../../../../ui/Toast/ToastContext";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../../__generated__/gql";
import styles from "./Outline.module.scss";
import { useMutation, useQuery } from "urql";
import OutlineInput, { OutlineState } from "./OutlineInput/OutlineInput";
import useHistoryState from "../../../../../util/useHistoryState";
import { arrayMove } from "@dnd-kit/sortable";
import GeneratingBanner from "./GeneratingBanner";
import {
  ArticleStatus,
  GenerationStep,
} from "../../../../../__generated__/gql/graphql";
import uuidv4 from "../../../../../ui/util/uuidv4";
import Button from "../../../../../ui/Button/Button";
import { AlertTriangle } from "../../../../../ui/Icon/Icon";
import Modal from "../../../../../ui/Modal/Modal";
import GenerateModal, {
  GenerateArticleModalFormData,
} from "../../../../../components/GenerateModal/GenerateModal";

export const EditArticleOutlineFragment = graphql(/* GraphQL */ `
  fragment EditArticleOutline on Article {
    id
    title
    status
    version
    processingUntilStep
    sections {
      id
      title
      titleDisabled
      description
      sectionNumber
    }
  }
`);

const ArticleOutlineQuery = graphql(/* GraphQL */ `
  query articleOutline($id: String!) {
    article(id: $id) {
      ...EditArticleOutline
    }
  }
`);

const EditArticleOutlineMutation = graphql(/* GraphQL */ `
  mutation editArticleOutline($input: UpdateArticleInput!) {
    updateArticle(input: $input) {
      ...EditArticleOutline
    }
  }
`);

const GenerateFromOutlineMutation = graphql(/* GraphQL */ `
  mutation generateFromOutline($id: String!, $regenerateContent: Boolean!) {
    generateArticle(
      input: {
        id: $id
        regenerateBrief: false
        regenerateOutline: false
        regenerateContent: $regenerateContent
      }
    ) {
      ... on Article {
        id
        status
      }
      ... on InsufficientCreditsError {
        __typename
        message
      }
    }
  }
`);

const ConfirmChangesModal = ({
  status,
  isOpen,
  onClose,
  onSubmit,
  submitting,
  creditsRequired,
}: {
  status: ArticleStatus;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: GenerateArticleModalFormData) => Promise<void>;
  submitting: boolean;
  creditsRequired?: number;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirm changes">
      <GenerateModal
        onClose={onClose}
        onSubmit={async (data) => {
          await onSubmit(data);
          onClose();
        }}
        fetching={submitting}
        confirmationText="Confirm"
        generationOptions={{
          open: true,
          hideBrief: true,
          hideOutline: true,
          hideLinks: true,
        }}
        defaultValues={{
          regenerate: true,
          regenerateBrief: false,
          regenerateOutline: false,
          regenerateContent: true,
          regenerateLinks: true,
        }}
        articleCounts={{
          total: 1,
          generated:
            status === ArticleStatus.Generated ||
            status === ArticleStatus.Published
              ? 1
              : 0,
          processing: status === ArticleStatus.Processing ? 1 : 0,
          published: status === ArticleStatus.Published ? 1 : 0,
          draft: status === ArticleStatus.Draft ? 1 : 0,
        }}
        creditsRequired={creditsRequired ?? 0}
      >
        <div className={styles.confirmationModal}>
          <AlertTriangle
            size="large"
            className={styles.confirmationModalIcon}
          />
          <div>
            <p>
              Changes to the outline will not automatically update the article
              content. It is recommended to regenerate the content when changes
              to an outline are made.
            </p>
          </div>
        </div>
      </GenerateModal>
    </Modal>
  );
};

function Outline({
  onClose,
  isOpen,
  data,
  onChange,
  creditsRequired,
}: {
  onClose?: () => void;
  isOpen?: boolean;
  data: FragmentType<typeof EditArticleOutlineFragment>;
  onChange?: (isDirty: boolean) => void;
  creditsRequired: number;
}) {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const article = getFragmentData(EditArticleOutlineFragment, data);
  const { showToast } = useToast();
  const [, editArticleOutline] = useMutation(EditArticleOutlineMutation);
  const [, generateFromOutlineMutation] = useMutation(
    GenerateFromOutlineMutation,
  );

  const [articleOutlineQueryResponse, refetchArticleOutlineQuery] = useQuery({
    query: ArticleOutlineQuery,
    variables: { id: article.id },
    pause: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const previousArticleStatus = useRef(article.status);

  const { state, set, undo, redo, clear, canUndo } =
    useHistoryState<OutlineState>({
      initialValue: {
        id: article.id,
        title: article.title ?? "",
        sections: article.sections
          ?.sort((a, b) => a.sectionNumber - b.sectionNumber)
          .map((section) => ({
            id: section.id,
            internalId: section.id,
            title: section.title,
            titleDisabled: section.titleDisabled,
            description: section.description ?? undefined,
            isExpanded: false,
          })) || [
          {
            internalId: uuidv4(),
            title: "",
            titleDisabled: false,
            isExpanded: true,
          },
        ],
      },
    });

  useEffect(() => {
    onChange?.(canUndo);
  }, [canUndo, onChange]);

  useEffect(() => {
    const undoRedoEventListener = (event: KeyboardEvent) => {
      if (event.key === "z" && event.metaKey) {
        event.preventDefault();

        if (event.shiftKey) {
          // CTRL + SHIFT + Z
          redo();
        } else {
          // CTRL + Z
          undo();
        }
      }
    };

    window.addEventListener("keydown", undoRedoEventListener);

    return () => {
      window.removeEventListener("keydown", undoRedoEventListener);
    };
  }, [redo, undo]);

  function moveNode(nodeId: string, beforeNodeId: string) {
    set((previous) => ({
      ...previous,
      sections: arrayMove(
        previous.sections,
        previous.sections.findIndex((section) => section.internalId === nodeId),
        previous.sections.findIndex(
          (section) => section.internalId === beforeNodeId,
        ),
      ),
    }));
  }

  const onSubmit = useCallback(
    async (options?: GenerateArticleModalFormData) => {
      setIsSubmitting(true);

      const response = await editArticleOutline({
        input: {
          id: article.id,
          title: state.title,
          sections: state.sections
            .filter((section) => !!section.title)
            .map((section, index) => ({
              id: section.id ?? "",
              title: section.title,
              description: section.description,
              sectionNumber: index + 1,
            })),
        },
      });

      const responseData = getFragmentData(
        EditArticleOutlineFragment,
        response.data?.updateArticle,
      );

      if (options?.regenerateContent) {
        const generateArticleResponse = await generateFromOutlineMutation({
          id: article.id,
          regenerateContent: options.regenerateContent,
        });

        if (
          generateArticleResponse.data?.generateArticle.__typename ===
          "InsufficientCreditsError"
        ) {
          showToast({
            variant: "error",
            title: "You've run out of article credits",
          });
        }
      }

      if (responseData) {
        clear({
          id: responseData.id,
          title: responseData.title ?? "",
          sections: responseData.sections
            ?.sort((a, b) => a.sectionNumber - b.sectionNumber)
            .map((section) => ({
              id: section.id,
              internalId: section.id,
              title: section.title,
              titleDisabled: section.titleDisabled,
              description: section.description ?? undefined,
              isExpanded: false,
            })) || [
            {
              internalId: uuidv4(),
              title: "",
              titleDisabled: false,
              isExpanded: true,
            },
          ],
        });

        showToast({ title: "Article updated" });
      }

      setIsSubmitting(false);
    },
    [
      article.id,
      clear,
      editArticleOutline,
      generateFromOutlineMutation,
      showToast,
      state.sections,
      state.title,
    ],
  );

  const processing = article.status === ArticleStatus.Processing;
  const regenerating = article.version > 1;
  const generating =
    !article.sections?.length &&
    processing &&
    (!article.processingUntilStep ||
      article.processingUntilStep === GenerationStep.Content);

  useEffect(() => {
    const articleOutline = getFragmentData(
      EditArticleOutlineFragment,
      articleOutlineQueryResponse.data?.article,
    );

    if (
      previousArticleStatus.current === ArticleStatus.Processing &&
      article.status !== ArticleStatus.Processing
    ) {
      refetchArticleOutlineQuery();
    }

    previousArticleStatus.current = article.status;

    if (
      articleOutline &&
      articleOutline.status !== ArticleStatus.Processing &&
      !articleOutline.processingUntilStep
    ) {
      clear({
        id: article.id,
        title: article.title ?? "",
        sections: article.sections
          ?.sort((a, b) => a.sectionNumber - b.sectionNumber)
          .map((section) => ({
            id: section.id,
            internalId: section.id,
            title: section.title,
            titleDisabled: section.titleDisabled,
            description: section.description ?? undefined,
            isExpanded: false,
          })) || [
          {
            internalId: uuidv4(),
            title: "",
            titleDisabled: false,
            isExpanded: true,
          },
        ],
      });
    }
  }, [
    article,
    articleOutlineQueryResponse.data,
    clear,
    refetchArticleOutlineQuery,
  ]);

  return (
    <div className={styles.form}>
      <ConfirmChangesModal
        status={article.status}
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onSubmit={onSubmit}
        submitting={isSubmitting}
        creditsRequired={creditsRequired}
      />
      {onClose ? (
        <ConfirmationModal
          sticky
          className={styles.formFooter}
          loading={isSubmitting || !isOpen}
          onClose={onClose}
          onSubmit={() =>
            article.status === ArticleStatus.Draft
              ? onSubmit()
              : setIsConfirmationOpen(true)
          }
          confirmationText="Save"
          confirmDisabled={processing || !canUndo}
        >
          {processing && <GeneratingBanner id={article.id} />}

          <Label
            label="Article outline"
            tooltip="Manage the outline of sections that are included in the article. Clicking the arrow next to a section title will allow you to edit the description of the section, which will act as instructions for Macaw when generating the section."
          >
            {null}
          </Label>

          <OutlineInput
            state={state}
            set={set}
            moveNode={moveNode}
            processing={processing}
            generating={generating}
            regenerating={regenerating}
            creditsRequired={creditsRequired}
          />
        </ConfirmationModal>
      ) : (
        <>
          {processing && <GeneratingBanner id={article.id} />}

          <Label
            label="Article outline"
            tooltip="Manage the outline of sections that are included in the article. Clicking the arrow next to a section title will allow you to edit the description of the section, which will act as instructions for Macaw when generating the section."
          >
            {null}
          </Label>

          <OutlineInput
            state={state}
            set={set}
            moveNode={moveNode}
            processing={processing}
            generating={generating}
            regenerating={regenerating}
            creditsRequired={creditsRequired}
          />

          <div className={styles.buttonGroup}>
            <Button
              variant="outline"
              size="small"
              onClick={() => clear()}
              disabled={!canUndo || isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="small"
              disabled={!canUndo || isSubmitting}
              loading={isSubmitting}
              type="submit"
              onClick={() =>
                article.status === ArticleStatus.Draft
                  ? onSubmit()
                  : setIsConfirmationOpen(true)
              }
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default Outline;
